/**
 * Lógica pura de fiduciantes y participación.
 * Porcentajes: texto con hasta 4 decimales. Para sumar se pasan a ENTEROS escalados a 10^4
 * ("33,3334" → 333334): nunca float.
 */
import type { AtrasadoDistribucion, ClaseFiduciante, Distribucion, EstadoPortal, Fiduciante, ItemDistribucion, LineaAtrasado, TipoFiduciante } from "./api";
import { formatCuit } from "./fideicomisos";
import { formatFecha, formatMoneda, parseFecha } from "./format";

export const ESCALA = 10_000;
export const CIEN = 100 * ESCALA;

const RE_PORCENTAJE = /^(\d{1,3})(?:[.,](\d{1,4}))?$/;

/** "33,3334" | "33.3334" → 333334. null si no es un número de hasta 3 enteros y 4 decimales. */
export function aEscalado(texto: string): number | null {
  const m = RE_PORCENTAJE.exec(texto.trim());
  if (!m) return null;
  return Number(m[1]) * ESCALA + Number((m[2] ?? "").padEnd(4, "0"));
}

/** 333334 → "33.3334" (formato de la API). */
export function aTextoApi(escalado: number): string {
  const resto = escalado % ESCALA;
  return `${(escalado - resto) / ESCALA}.${String(resto).padStart(4, "0")}`;
}

/** 975000 → "97,5000" */
export function formatEscalado(escalado: number): string {
  return aTextoApi(escalado).replace(".", ",");
}

/** 250000 → "25"; 205000 → "20,5" (sin ceros de más, para precargar un PercentField). */
export function aEditable(escalado: number): string {
  return formatEscalado(escalado).replace(/,?0+$/, "");
}

/** Reparte 100 % en partes iguales; lo que no entra en 4 decimales va al primero (dos: mitad y mitad). */
export function partesIguales<T extends { porcentaje: string }>(lineas: T[]): T[] {
  if (lineas.length === 0) return lineas;
  const parte = Math.floor(CIEN / lineas.length);
  const resto = CIEN - parte * lineas.length;
  return lineas.map((l, i) => ({ ...l, porcentaje: aEditable(parte + (i === 0 ? resto : 0)) }));
}

/** "25.0000" → "25 %"; "33.3334" → "33,3334 %"; null → "—". */
export function formatParticipacion(porcentaje: string | null): string {
  if (porcentaje === null) return "—";
  const e = aEscalado(porcentaje);
  return e === null ? porcentaje : `${aEditable(e)} %`;
}

/** Suma exacta (escalada). Las filas vacías no cuentan; null si alguna no es un porcentaje válido. */
export function sumar(items: { porcentaje: string }[]): number | null {
  let total = 0;
  for (const item of items) {
    if (item.porcentaje.trim() === "") continue;
    const e = aEscalado(item.porcentaje);
    if (e === null) return null;
    total += e;
  }
  return total;
}

/** "Suma: 97,5000% (faltan 2,5000%)" */
export function mensajeSuma(total: number): string {
  const base = `Suma: ${formatEscalado(total)}%`;
  if (total === CIEN) return base;
  return total < CIEN ? `${base} (faltan ${formatEscalado(CIEN - total)}%)` : `${base} (sobran ${formatEscalado(total - CIEN)}%)`;
}

/** Fecha de hoy en Argentina (UTC-3) en ISO ("2026-09-25"). El backend usa esa zona horaria. */
export function hoyIso(fecha: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Buenos_Aires",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(fecha);
}

/** "20123456789" → "20-12345678-9"; "12345678" → "12.345.678"; null → "—". */
export function formatDocumento(documento: string | null): string {
  if (!documento) return "—";
  if (documento.length === 11) return formatCuit(documento);
  return documento.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// ------------------------------------------------------------------ formulario de distribución

/** Una línea agregada: un fiduciante en una clase. `valor` es el %, tal cual se escribió (A y B igual). */
export interface LineaDistribucion {
  fiduciante_id: number;
  nombre: string;
  clase: ClaseFiduciante;
  valor: string;
}

/** La línea que se está armando (fiduciante elegido en el buscador, clase y valor) antes de tocar Agregar. */
export interface LineaNueva {
  fiduciante_id: number | null;
  clase: ClaseFiduciante;
  valor: string;
}

export const LINEA_VACIA: LineaNueva = {
  fiduciante_id: null,
  clase: "A",
  valor: "",
};

export interface FormDistribucion {
  vigente_desde: string; // dd/mm/aaaa
  nota: string;
  lineas: LineaDistribucion[];
}

export interface ErroresDistribucion {
  vigente_desde?: string;
  suma?: string;
}

export interface ErroresLinea {
  fiduciante?: string;
  valor?: string;
}

export const OPCIONES_CLASE: { valor: ClaseFiduciante; etiqueta: string }[] = [
  { valor: "A", etiqueta: "Clase A" },
  { valor: "B", etiqueta: "Clase B" },
];

export interface ItemPayload {
  fiduciante_id: number;
  clase: ClaseFiduciante;
  porcentaje: string;
}

export interface PayloadDistribucion {
  vigente_desde: string;
  nota: string | null;
  items: ItemPayload[];
}

// ------------------------------------------------------------------ totales (A y B llevan el % a mano, sobre el total)

/** % de una línea (escalado a 10^4). null si no es válido. */
export function porcentajeLinea(linea: { valor: string }): number | null {
  return aEscalado(linea.valor);
}

export interface TotalesDistribucion {
  a: number; // escalados a 10^4
  b: number;
  total: number;
}

/** Suma de las líneas por clase. null si alguna no es válida. */
export function totalesDistribucion(form: FormDistribucion): TotalesDistribucion | null {
  let a = 0;
  let b = 0;
  for (const linea of form.lineas) {
    const pct = porcentajeLinea(linea);
    if (pct === null) return null;
    if (linea.clase === "A") a += pct;
    else b += pct;
  }
  return { a, b, total: a + b };
}

/** Lo que falta para 100 (escalado a 10^4; negativo si se pasa). Una línea inválida no cuenta. */
export function restante(form: FormDistribucion): number {
  return CIEN - form.lineas.reduce((suma, l) => suma + (porcentajeLinea(l) ?? 0), 0);
}

/** "Queda por repartir: 40 %" · "Repartido el 100 %" · si se pasa, cuánto sobra. */
export function mensajeRestante(total: number): string {
  if (total === CIEN) return "Repartido el 100 %";
  if (total > CIEN) return mensajeSuma(total);
  return `Queda por repartir: ${formatParticipacion(aTextoApi(CIEN - total))}`;
}

/** "Clase A: 90 % · Clase B: 10 %"; null sin clase B. */
export function mensajeTotales(t: TotalesDistribucion): string | null {
  if (t.b === 0) return null;
  return `Clase A: ${formatParticipacion(aTextoApi(t.a))} · Clase B: ${formatParticipacion(aTextoApi(t.b))}`;
}

export const ETIQUETA_CLASE: Record<ClaseFiduciante, string> = {
  A: "Clase A",
  B: "Clase B",
};

/** Una línea por item de `base` (en su orden), o ninguna si no hay base. */
export function formDistribucion(base: Distribucion | null, fechaIso: string, nota = ""): FormDistribucion {
  return {
    vigente_desde: formatFecha(fechaIso),
    nota,
    lineas: (base?.items ?? []).map((i) => {
      const e = aEscalado(i.porcentaje);
      const valor = e === null ? i.porcentaje : aEditable(e);
      return {
        fiduciante_id: i.fiduciante_id,
        nombre: i.nombre,
        clase: i.clase,
        valor,
      };
    }),
  };
}

const clasesDe = (form: FormDistribucion, fiduciante_id: number) => form.lineas.filter((l) => l.fiduciante_id === fiduciante_id).map((l) => l.clase);

/** Opciones del buscador: saca a quien ya está en las dos clases; al que está en una, se lo avisa. */
export function opcionesFiduciantes(fiduciantes: { id: number; nombre: string }[], form: FormDistribucion) {
  return fiduciantes.flatMap((f) => {
    const clases = clasesDe(form, f.id);
    if (clases.length >= 2) return [];
    const [clase] = clases;
    return [
      {
        valor: String(f.id),
        etiqueta: f.nombre,
        ...(clase ? { detalle: `Ya está como clase ${clase}` } : {}),
      },
    ];
  });
}

/** La clase que el fiduciante todavía no tiene en la distribución (A si no tiene ninguna). */
export function claseLibre(form: FormDistribucion, fiduciante_id: number): ClaseFiduciante {
  return clasesDe(form, fiduciante_id).includes("A") ? "B" : "A";
}

function errorValor(nueva: LineaNueva): string | null {
  const v = nueva.valor.trim();
  if (v === "") return "Poné el porcentaje";
  const e = aEscalado(v);
  if (e === null) return "Hasta 3 enteros y 4 decimales";
  if (e === 0) return "Tiene que ser mayor a 0";
  return null;
}

/** Errores de la línea a agregar: fiduciante elegido y no repetido en la clase, valor válido y sin pasarse de 100. */
export function validarLinea(nueva: LineaNueva, form: FormDistribucion): ErroresLinea {
  const errores: ErroresLinea = {};
  if (nueva.fiduciante_id === null) errores.fiduciante = "Elegí un fiduciante";
  else if (clasesDe(form, nueva.fiduciante_id).includes(nueva.clase)) errores.fiduciante = `Ya está como clase ${nueva.clase}`;
  const valor = errorValor(nueva);
  if (valor) errores.valor = valor;
  else {
    const queda = restante(form);
    if ((porcentajeLinea(nueva) ?? 0) > queda)
      errores.valor = queda <= 0 ? "Ya está repartido el 100 %" : `Quedan ${formatParticipacion(aTextoApi(queda))} por repartir`;
  }
  return errores;
}

/** Suma la línea al final. Asume que `validarLinea` no devolvió errores. */
export function agregarLinea(form: FormDistribucion, nueva: LineaNueva, nombre: string): FormDistribucion {
  return {
    ...form,
    lineas: [
      ...form.lineas,
      {
        fiduciante_id: nueva.fiduciante_id ?? 0,
        nombre,
        clase: nueva.clase,
        valor: nueva.valor.trim(),
      },
    ],
  };
}

export function quitarLinea(form: FormDistribucion, indice: number): FormDistribucion {
  return { ...form, lineas: form.lineas.filter((_, i) => i !== indice) };
}

/** Saca la línea y la devuelve como línea nueva, para cambiarla y volver a agregarla. */
export function sacarLinea(form: FormDistribucion, indice: number): { form: FormDistribucion; nueva: LineaNueva } {
  const l = form.lineas[indice];
  return {
    form: quitarLinea(form, indice),
    nueva: l ? { fiduciante_id: l.fiduciante_id, clase: l.clase, valor: l.valor } : LINEA_VACIA,
  };
}

export function validarDistribucion(form: FormDistribucion): ErroresDistribucion {
  const errores: ErroresDistribucion = {};
  if (parseFecha(form.vigente_desde) === null) errores.vigente_desde = "Usá el formato dd/mm/aaaa";
  if (form.lineas.length === 0) errores.suma = "Agregá al menos un fiduciante";
  else {
    // Menos de 100 vale: queda incompleta (borrador) y no rige hasta completarla. Más de 100, no.
    const t = totalesDistribucion(form);
    if (t === null) errores.suma = "Revisá los porcentajes";
    else if (t.total > CIEN) errores.suma = mensajeSuma(t.total);
  }
  return errores;
}

/** "Incompleta: faltan 12,5 %" (para una distribución que no suma 100). */
export function mensajeIncompleta(d: Distribucion): string {
  const e = aEscalado(d.suma) ?? 0;
  return `Incompleta: faltan ${formatParticipacion(aTextoApi(CIEN - e))}`;
}

/** Hay fiduciantes pero hoy no rige ninguna distribución completa: nadie tiene % vigente, así que los llamados
 *  y los gastos no se reparten. Uno recién cargado sin % no cuenta si otro sí lo tiene. */
export function sinDistribucionVigente(fiduciantes: { porcentaje_vigente: string | null }[]): boolean {
  return fiduciantes.length > 0 && fiduciantes.every((f) => f.porcentaje_vigente === null);
}

export const esValida = (errores: object) => Object.keys(errores).length === 0;

/** Cuerpo del POST/PUT. Asume que `validarDistribucion(form)` no devolvió errores. */
export function aPayloadDistribucion(form: FormDistribucion): PayloadDistribucion {
  return {
    vigente_desde: parseFecha(form.vigente_desde) ?? "",
    nota: form.nota.trim() || null,
    items: form.lineas.map(
      (l): ItemPayload => ({
        fiduciante_id: l.fiduciante_id,
        clase: l.clase,
        porcentaje: aTextoApi(aEscalado(l.valor) ?? 0),
      }),
    ),
  };
}

/** La API las manda de la más nueva a la más vieja. Las incompletas van aparte (no rigen);
 * programadas = completas posteriores a la vigente (o todas si no hay vigente). */
export function separarDistribuciones(lista: Distribucion[]): {
  incompletas: Distribucion[];
  vigente: Distribucion | null;
  programadas: Distribucion[];
  anteriores: Distribucion[];
} {
  const completas = lista.filter((d) => d.completa);
  const vigente = completas.find((d) => d.vigente) ?? null;
  return {
    incompletas: lista.filter((d) => !d.completa),
    vigente,
    programadas: completas.filter((d) => !d.vigente && (vigente === null || d.vigente_desde > vigente.vigente_desde)),
    anteriores: vigente ? completas.filter((d) => d.vigente_desde < vigente.vigente_desde) : [],
  };
}

// ------------------------------------------------------------------ formulario de fiduciante

export const ETIQUETA_TIPO: Record<TipoFiduciante, string> = {
  humana: "Persona humana",
  juridica: "Persona jurídica",
};
export const OPCIONES_TIPO = (Object.keys(ETIQUETA_TIPO) as TipoFiduciante[]).map((t) => ({ valor: t, etiqueta: ETIQUETA_TIPO[t] }));

export const ETIQUETA_PORTAL: Record<EstadoPortal, string> = {
  sin_acceso: "Sin acceso",
  activo: "Activo",
  invitacion_pendiente: "Invitación pendiente",
  primer_ingreso: "Falta el primer ingreso",
  temporal_vencida: "Contraseña vencida",
  desactivado: "Desactivado",
};

export const TONO_PORTAL: Record<EstadoPortal, "neutro" | "exito" | "alerta"> = {
  sin_acceso: "neutro",
  activo: "exito",
  invitacion_pendiente: "alerta",
  primer_ingreso: "alerta",
  temporal_vencida: "alerta",
  desactivado: "neutro",
};

export interface FormFiduciante {
  tipo: TipoFiduciante | "";
  nombre: string;
  documento: string;
  email: string;
  telefono: string;
  domicilio: string;
  notas: string;
}

export type ErroresFiduciante = Partial<Record<keyof FormFiduciante, string>>;

export interface PayloadFiduciante {
  tipo: TipoFiduciante;
  nombre: string;
  documento: string | null;
  email: string | null;
  telefono: string | null;
  domicilio: string | null;
  notas: string | null;
}

export function formFiduciante(f?: Fiduciante): FormFiduciante {
  return {
    tipo: f?.tipo ?? "",
    nombre: f?.nombre ?? "",
    documento: f?.documento ? formatDocumento(f.documento) : "",
    email: f?.email ?? "",
    telefono: f?.telefono ?? "",
    domicilio: f?.domicilio ?? "",
    notas: f?.notas ?? "",
  };
}

const digitosDocumento = (documento: string) => documento.replace(/[.\-\s]/g, "");

/** Mismas reglas que el backend (el backend es la autoridad; esto evita el viaje). */
const emailValido = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

/** El email que se pide al dar acceso a una ficha que no lo tenía: acá es obligatorio. */
export function validarEmailAcceso(email: string): string | null {
  if (!email.trim()) return "Poné el email";
  return emailValido(email) ? null : "Revisá el email";
}

export function validarFiduciante(f: FormFiduciante): ErroresFiduciante {
  const errores: ErroresFiduciante = {};
  if (f.tipo === "") errores.tipo = "Elegí el tipo";
  if (!f.nombre.trim()) errores.nombre = "Poné el nombre";
  const doc = digitosDocumento(f.documento);
  if (doc) {
    if (!/^\d+$/.test(doc)) errores.documento = "El documento sólo puede tener números, puntos y guiones";
    else if (f.tipo === "juridica" && doc.length !== 11) errores.documento = "El CUIT tiene que tener 11 dígitos";
    else if (f.tipo === "humana" && ![7, 8, 11].includes(doc.length)) errores.documento = "Poné un DNI de 7 u 8 dígitos o un CUIT/CUIL de 11";
  }
  if (f.email.trim() && !emailValido(f.email)) errores.email = "Revisá el email";
  return errores;
}

const vacioANull = (s: string) => (s.trim() === "" ? null : s.trim());

/** Cuerpo del POST/PATCH. Asume que `validarFiduciante(f)` no devolvió errores. */
export function aPayloadFiduciante(f: FormFiduciante): PayloadFiduciante {
  return {
    tipo: f.tipo as TipoFiduciante,
    nombre: f.nombre.trim(),
    documento: vacioANull(digitosDocumento(f.documento)),
    email: vacioANull(f.email),
    telefono: vacioANull(f.telefono),
    domicilio: vacioANull(f.domicilio),
    notas: vacioANull(f.notas),
  };
}

// ------------------------------------------------------------------ torta

export interface PorcionTorta {
  clave: string;
  etiqueta: string;
  valor: number; // escalado a 10^4 (entero)
  color: string;
}

/** Paleta categórica validada (orden fijo): los 8 tonos base y, desde el 9.º fiduciante, un segundo grupo con otros
 *  pasos de los mismos tonos. Con más de 16 se repite: la identidad la dan la leyenda, el tooltip y la tabla. */
export const COLORES_SERIE = [
  "#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948",
  "#6da7ec", "#a8431b", "#0ca30c", "#a8386a", "#c98500", "#0f7a55", "#9085e9", "#9c2a2a",
] as const;

export function datosTorta(items: ItemDistribucion[]): PorcionTorta[] {
  const total = COLORES_SERIE.length;
  return items.map((i, n) => {
    // La última porción toca a la primera: si al repetir la paleta le tocara el mismo color, usa el siguiente.
    const cierraConElPrimero = n > 0 && n === items.length - 1 && n % total === 0;
    return {
      clave: i.clase === "B" ? `${i.fiduciante_id}-B` : String(i.fiduciante_id),
      etiqueta: i.clase === "B" ? `${i.nombre} (clase B)` : i.nombre,
      valor: aEscalado(i.porcentaje) ?? 0,
      color: COLORES_SERIE[(cierraConElPrimero ? n + 1 : n) % total] ?? COLORES_SERIE[0],
    };
  });
}

// ------------------------------------------------------------------ lo atrasado de un cambio (Mati, 2026-10-18)

const sinSigno = (t: string) => t.trim().replace(/^-/, "");

/** "Juan sube 3 %: debe USD 3.000,00 de lo atrasado" / "Ana baja 2 %: recibe USD 2.000,00". */
export function textoAtrasado(l: LineaAtrasado): string {
  const pct = formatParticipacion(sinSigno(l.diferencia));
  const monto = formatMoneda(sinSigno(l.monto_usd), "USD");
  return l.monto_usd.trim().startsWith("-") ? `${l.nombre} sube ${pct}: debe ${monto} de lo atrasado` : `${l.nombre} baja ${pct}: recibe ${monto}`;
}

/** La línea de contexto, o null si el cambio no mueve plata (sin gastos antes, sin diferencias o la primera). */
export function resumenAtrasado(a: AtrasadoDistribucion | null | undefined): string | null {
  if (!a || a.lineas.length === 0 || a.desde_anterior === null) return null;
  return `Comparada con la distribución del ${formatFecha(a.desde_anterior)}, sobre ${formatMoneda(a.base_usd, "USD")} de gastos ya repartidos.`;
}
